// Crescent Edge (owner approved the concept 2026-11-02: 'Make them all'): energy blades form in both hands and Aren throws
// crescent waves, hands alternating, that cut straight through lines of foes. Render-only: reads sim.crescents /
// sim.crescentThrow and the 'crescent' / 'crescentEnd' events; decides nothing about gameplay.
//  - wave: a sharp, flat crescent skimming above the ground: a white-hot leading edge over a violet body that fades toward
//    the concave back, needle tips, and a fading after-image trail of echoes behind it. Left-hand and right-hand waves tilt
//    opposite ways. Lunar Cross: two crossed crescents flying as one X (the sim's X-shaped cutting front).
//  - blades: a small glowing crescent blade at each hand while a throw is under way; each wave leaves the hand that threw
//    it (eases onto the sim path) with the approved Spirit Bolt arm thrust (bolt_r / bolt_l)
//  - cut sparks along the slash on every foe it passes, a violet pool of light under it, a quick fade-out at full range
// Distinct from Scythe Disc (a yellow spinning disc that flies out and back): a thin moon-violet slash flying straight.
// One instanced mesh; no per-frame allocations.
import * as THREE from 'three/webgpu';
import { abs, attribute, float, mix, smoothstep, uv, vec3 } from 'three/tsl';
import { CRESCENT_X_TILT, type Sim, type SimEvent } from '../core/sim';
import { CRESCENT_BOW } from '../core/skillStats';
import type { Fx } from './fx';
import { FLASH } from './fx';
import type { GroundGlow } from './groundGlow';
import type { GltfHero } from './heroGltf';
import type { HeroRig } from './models';

const SEGS = 24;
const THICK = 0.24;            // crescent thickness at the middle (share of its width)
const ECHOES = 4, ECHO_GAP = 0.55; // after-images behind each wave (m apart)
const CAP = 24 * 2 * (ECHOES + 1) + 4 + 24 * 2;
const FLY_Y = 1.0;             // height above the ground the waves skim at
const EASE_T = 0.09;           // a fresh wave eases from the hand onto its sim path over this (s)
const TILT = 0.32;             // roll of a wave (rad), opposite for each hand
const BODY = new THREE.Color(0x6a3cff), EDGE = new THREE.Color(0xf2eeff), DEEP = new THREE.Color(0x24107a);
export const CRESCENT_GLOW = 0xa48cff;

type WaveEv = Extract<SimEvent, { t: 'crescent' }>;
interface Rec { ox: number; oy: number; oz: number; age: number }
interface Dying { on: boolean; x: number; y: number; z: number; dx: number; dz: number; w: number; hand: number; evo: boolean; t: number }

const _m = new THREE.Matrix4(), _x = new THREE.Vector3(), _y = new THREE.Vector3(), _z = new THREE.Vector3(), _p = new THREE.Vector3();
const _h = new THREE.Vector3(), _f = new THREE.Vector3(), _q = new THREE.Quaternion(), _up = new THREE.Vector3(0, 1, 0);

/** flat crescent, chord along local z (tips at z = ±0.5), bulging toward +x; uv.x across (0..1), uv.y 0 back edge → 1 front */
function crescentGeometry(): THREE.BufferGeometry {
  const pos: number[] = [], uvs: number[] = [], idx: number[] = [];
  for (let i = 0; i <= SEGS; i++) {
    const q = (i / SEGS) * 2 - 1, k = 1 - q * q, lead = CRESCENT_BOW * k, back = lead - THICK * Math.pow(Math.max(0, k), 0.8);
    pos.push(back, 0, q * 0.5, lead, 0, q * 0.5); uvs.push(i / SEGS, 0, i / SEGS, 1);
  }
  for (let i = 0; i < SEGS; i++) { const v = i * 2; idx.push(v, v + 1, v + 2, v + 2, v + 1, v + 3); }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3)); g.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2)); g.setIndex(idx);
  return g;
}

export class CrescentEdgeFx {
  /** QA counters */
  thrown = 0; ended = 0; blades = 0;
  private mesh: THREE.InstancedMesh;
  private attr: Float32Array; private attrB: THREE.InstancedBufferAttribute;
  private recs = new Map<number, Rec>();
  private dying: Dying[] = Array.from({ length: 24 }, () => ({ on: false, x: 0, y: 0, z: 0, dx: 0, dz: 1, w: 1, hand: 1, evo: false, t: 0 }));
  private bladeT = 0; private bladeEvo = false;
  private hands = [new THREE.Vector3(), new THREE.Vector3()]; private handsOk = false;

  constructor(scene: THREE.Scene, private fx: Fx, private groundAt: (x: number, z: number) => number, private hero: () => GltfHero | null) {
    const geo = crescentGeometry();
    this.attr = new Float32Array(CAP * 4);
    this.attrB = new THREE.InstancedBufferAttribute(this.attr, 4).setUsage(THREE.DynamicDrawUsage) as THREE.InstancedBufferAttribute;
    geo.setAttribute('crA', this.attrB);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const a = attribute('crA', 'vec4') as any; // alpha, edge strength, body brightness, unused
    const v = uv().y, q = abs(uv().x.mul(2).sub(1));
    const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, fog: false });
    const edge = smoothstep(0.8, 0.93, v).mul(a.y);                       // white-hot cutting edge (hard cel band)
    const lav = mix(vec3(DEEP.r, DEEP.g, DEEP.b), vec3(BODY.r * 2.2, BODY.g * 1.6, BODY.b * 1.5), smoothstep(0.05, 0.78, v));
    m.colorNode = mix(lav.mul(a.z), vec3(EDGE.r * 1.5, EDGE.g * 1.5, EDGE.b * 1.6), edge);
    const tips = smoothstep(1.0, 0.82, q).mul(0.6).add(0.4);
    m.opacityNode = smoothstep(0.0, 0.32, v).mul(0.92).add(edge.mul(0.1)).mul(smoothstep(1.0, 0.975, v).mul(0.6).add(0.4)).mul(tips).mul(a.x).min(1).mul(float(1));
    this.mesh = new THREE.InstancedMesh(geo, m, CAP); this.mesh.count = 0; this.mesh.frustumCulled = false; this.mesh.renderOrder = 6;
    scene.add(this.mesh);
  }

  /** 'crescent' event: the wave leaves a hand (arm thrust; it is drawn from that hand) */
  throw(ev: WaveEv): void {
    this.thrown++; this.bladeT = 0.35; this.bladeEvo = ev.evolved;
    const h = this.hero(), side: 'R' | 'L' = ev.hand > 0 ? 'R' : 'L';
    let hx = ev.x, hy = this.groundAt(ev.x, ev.z) + 1.2, hz = ev.z;
    if (h && h.ready && h.root.visible) {
      if (h.allows(side === 'R' ? 'bolt_r' : 'bolt_l')) h.playAction(side === 'R' ? 'bolt_r' : 'bolt_l', Math.atan2(ev.dx, ev.dz));
      h.handWorld(side, _h); hx = _h.x; hy = _h.y; hz = _h.z;
    } else if (this.handsOk) { const hh = this.hands[ev.hand > 0 ? 0 : 1]; hx = hh.x; hy = hh.y; hz = hh.z; }
    this.recs.set(ev.id, { ox: hx - ev.x, oy: hy, oz: hz - ev.z, age: 0 });
    this.fx.stars.add(hx, hy, hz, ev.evolved ? 1.6 : 1.1, 0.08, CRESCENT_GLOW, 2.6);
    for (let k = 0; k < 6; k++) this.fx.particles.emitDir(hx, hy, hz, ev.dx * (6 + Math.random() * 5) + (Math.random() - 0.5) * 3, Math.random() * 2, ev.dz * (6 + Math.random() * 5) + (Math.random() - 0.5) * 3, k % 2 ? 0xffffff : CRESCENT_GLOW, 0.16, 0.18, 2.4);
    if (FLASH.dim >= 1) this.fx.lights.flash(hx, hy, hz, 0x9a7aff, 5, 0.08, 5);
  }

  /** 'crescentEnd': the wave reached full range: it flares wider and fades */
  end(ev: Extract<SimEvent, { t: 'crescentEnd' }>, sim: Sim): void {
    this.ended++; this.recs.delete(ev.id);
    const w = sim.crescents.find((q) => q.id === ev.id), d = this.dying.find((q) => !q.on); if (!w || !d) return;
    Object.assign(d, { on: true, x: ev.x, y: this.groundAt(ev.x, ev.z) + FLY_Y, z: ev.z, dx: w.dx, dz: w.dz, w: w.w, hand: w.hand, evo: w.evo, t: 0 });
  }

  /** a foe was cut: a thin white slash spark across the wave's path */
  hit(x: number, y: number, z: number, sim: Sim): void {
    let best = null as Sim['crescents'][number] | null, bd = 1e9;
    for (const w of sim.crescents) { if (!w.alive) continue; const d = (w.x - x) ** 2 + (w.z - z) ** 2; if (d < bd) { bd = d; best = w; } }
    const dx = best ? best.dx : 1, dz = best ? best.dz : 0;
    this.fx.stars.add(x, y + 0.3, z, 0.9, 0.07, 0xffffff, 2.4);
    for (let k = 0; k < 5; k++) { const s = (Math.random() - 0.5) * 2; this.fx.particles.emitDir(x, y + 0.3, z, -dz * s * 7 + dx * 3, 0.5 + Math.random() * 2, dx * s * 7 + dz * 3, k % 2 ? 0xffffff : CRESCENT_GLOW, 0.15, 0.16, 2.4); }
  }

  /** one crescent instance: centre (x,y,z), travel (dx,dz), width w, yaw offset (X arms), roll, alpha/edge/body */
  private put(n: number, x: number, y: number, z: number, dx: number, dz: number, w: number, yaw: number, roll: number, al: number, edge: number, body: number, bk = 1): number {
    if (n >= CAP || al <= 0.01) return n;
    const c = Math.cos(yaw), s = Math.sin(yaw), fx = dx * c - dz * s, fz = dz * c + dx * s; // turned travel direction
    _x.set(fx, 0, fz); _z.set(-fz, 0, fx);                                                     // bulge / chord axes
    _q.setFromAxisAngle(_x, roll); _z.applyQuaternion(_q); _y.crossVectors(_z, _x).normalize();
    _m.makeBasis(_x.multiplyScalar(w * bk), _y, _z.multiplyScalar(w)); _m.setPosition(x, y, z);
    this.mesh.setMatrixAt(n, _m);
    this.attr[n * 4] = al; this.attr[n * 4 + 1] = edge; this.attr[n * 4 + 2] = body;
    return n + 1;
  }
  /** a wave (both arms when evolved) with its echoes */
  private wave(n: number, x: number, y: number, z: number, dx: number, dz: number, w: number, hand: number, evo: boolean, al: number, echoes: number, grow = 1): number {
    const arms = evo ? [CRESCENT_X_TILT, -CRESCENT_X_TILT] : [0], ww = evo ? w / Math.cos(CRESCENT_X_TILT) : w;
    for (let e = 0; e <= echoes; e++) {
      const k = 1 - e / (ECHOES + 1), ex = x - dx * e * ECHO_GAP, ez = z - dz * e * ECHO_GAP, ea = al * (e ? (evo ? 0.28 : 0.42) * k * k : 1);
      for (const yaw of arms) n = this.put(n, ex, y, ez, dx, dz, ww * grow * (e ? 0.97 ** e : 1), yaw, (evo ? yaw * 0.5 : hand * TILT), ea, e ? 0.15 : 1, e ? 0.9 : 1, evo ? 0.6 : 1);
    }
    return n;
  }

  update(dt: number, sim: Sim, rig: HeroRig, gltf: GltfHero | null): void {
    // hands (blade anchors): the authored hand bones, else the rig forearms
    this.handsOk = false; const need = this.bladeT > 0 || !!sim.crescentThrow; // hands only while a throw is under way
    if (!need) { /* idle */ } else if (gltf && gltf.ready && gltf.root.visible) { gltf.handWorld('R', this.hands[0]); gltf.handWorld('L', this.hands[1]); this.handsOk = true; }
    else { rig.root.updateMatrixWorld(true); rig.foreR.localToWorld(this.hands[0].set(0, -0.36, 0.06)); rig.foreL.localToWorld(this.hands[1].set(0, -0.36, 0.06)); this.handsOk = true; }
    let n = 0;
    for (const w of sim.crescents) {
      if (!w.alive) continue;
      let r = this.recs.get(w.id);
      if (!r) { r = { ox: 0, oy: this.groundAt(w.x, w.z) + FLY_Y, oz: 0, age: EASE_T }; this.recs.set(w.id, r); }
      r.age += dt;
      const e = Math.max(0, 1 - r.age / EASE_T), k = e * e, gy = this.groundAt(w.x, w.z) + FLY_Y;
      const x = w.x + r.ox * k, z = w.z + r.oz * k, y = gy + (r.oy - gy) * k;
      const grow = 0.55 + 0.45 * Math.min(1, r.age / 0.12); // snaps open as it leaves the hand
      n = this.wave(n, x, y, z, w.dx, w.dz, w.w, w.hand, w.evo, 1, Math.min(ECHOES, Math.floor(w.d / ECHO_GAP)), grow);
      if (Math.random() < 0.6) { // sparks shed from the tips
        const s = Math.random() < 0.5 ? 1 : -1, hw = w.w / 2;
        this.fx.particles.emitDir(x - w.dz * hw * s, y, z + w.dx * hw * s, -w.dx * 2, 0.3, -w.dz * 2, Math.random() < 0.5 ? 0xffffff : CRESCENT_GLOW, 0.14, 0.2, 2.2);
      }
    }
    for (const d of this.dying) {
      if (!d.on) continue;
      d.t += dt; const u = d.t / 0.18; if (u >= 1) { d.on = false; continue; }
      n = this.wave(n, d.x + d.dx * u * 0.8, d.y, d.z + d.dz * u * 0.8, d.dx, d.dz, d.w, d.hand, d.evo, (1 - u) ** 2, 0, 1 + 0.25 * u);
    }
    // hand blades while a throw is under way
    if (sim.crescentThrow) { this.bladeT = 0.35; this.bladeEvo = sim.crescentThrow.evo; }
    this.bladeT = Math.max(0, this.bladeT - dt);
    if (this.bladeT > 0 && this.handsOk) {
      this.blades++;
      const yaw = gltf && gltf.ready && gltf.root.visible ? gltf.root.rotation.y : rig.root.rotation.y, fx0 = Math.sin(yaw), fz0 = Math.cos(yaw);
      const al = Math.min(1, this.bladeT / 0.12), bw = this.bladeEvo ? 0.85 : 0.7;
      for (let s = 0; s < 2; s++) {
        const hh = this.hands[s], out = s ? -1 : 1; // blade edge faces forward and a little outward
        _f.set(fx0 - fz0 * out * 0.5, 0, fz0 + fx0 * out * 0.5).normalize();
        _p.copy(hh).addScaledVector(_f, 0.12); _p.y += 0.1;
        // vertical blade: chord up the forearm line, bulging forward
        _x.copy(_f); _z.copy(_up); _y.crossVectors(_z, _x).normalize();
        _m.makeBasis(_x.multiplyScalar(bw), _y, _z.multiplyScalar(bw)); _m.setPosition(_p.x, _p.y, _p.z);
        if (n < CAP) { this.mesh.setMatrixAt(n, _m); this.attr[n * 4] = al * (0.85 + 0.15 * Math.sin(performance.now() * 0.05 + s)); this.attr[n * 4 + 1] = 1; this.attr[n * 4 + 2] = 1.2; n++; }
        if (Math.random() < 0.35) this.fx.particles.emitDir(_p.x, _p.y + 0.2, _p.z, 0, 0.8, 0, CRESCENT_GLOW, 0.14, 0.2, 2);
      }
    }
    this.mesh.count = n; this.mesh.instanceMatrix.needsUpdate = true; this.attrB.needsUpdate = true;
    if (this.recs.size > 64) for (const id of this.recs.keys()) if (!sim.crescents.some((q) => q.alive && q.id === id)) this.recs.delete(id);
  }

  /** Ground glow: a violet strip under each wave (across its width) */
  glowPools(g: GroundGlow, sim: Sim): void {
    for (const w of sim.crescents) {
      if (!w.alive) continue;
      const hw = w.w * 0.45, sx = -w.dz * hw, sz = w.dx * hw, gy = this.groundAt(w.x, w.z);
      g.strip(w.x - sx, w.z - sz, w.x + sx, w.z + sz, gy + FLY_Y, 0.9, 0x7a4cff, 0.3, 1, 0.5);
    }
  }

  clear(): void { this.recs.clear(); for (const d of this.dying) d.on = false; this.mesh.count = 0; this.bladeT = 0; }
}
